import React, { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import styles from './DeviceStatistics.module.css';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { DatePicker } from 'antd';
import moment from 'moment';
import { Cell, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { darkMapStyles } from "../../../utils/constants/styles";
import { DevicesRepository } from '../../../repositories/devices';
import { toast } from 'sonner';
import { ReactiveBuilder } from '../../components/Loader/loader';
import DeviceEventsTable from '../../../components/DeviceEventsTable';
import useDeviceEvents from '../../../hooks/useDeviceEvents';

const mapContainerStyle = {
  width: '100%',
  height: '400px',
};

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const DeviceStatistics = () => {
  const [cookies] = useCookies();
  const location = useLocation();
  const navigate = useNavigate();
  const { deviceId } = useParams();
  const [device, setDevice] = useState(location.state?.device || {});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(moment());
  const [deviceStatistics, setDeviceStatistics] = useState(null);

  // Use our custom hook to handle real-time events
  const { events: deviceEvents, loading: eventsLoading, error: eventsError, connected } = useDeviceEvents(deviceId);

  const defaultLat = cookies.defaultLat || 0;
  const defaultLng = cookies.defaultLng || 0;

  const [mapZoom, setMapZoom] = useState(11);
  const [mapCenter, setMapCenter] = useState({
    lat: defaultLat,
    lng: defaultLng,
  });

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  useEffect(() => {
    const loadDeviceData = async () => {
      try {
        setLoading(true);
        const date = selectedDate.format('YYYY-MM-DD');
        const response = await DevicesRepository.getDeviceStatisticsData(deviceId, date);

        if (!response.success) {
          toast.error(response.error || response.message);
          setError(response.error || response.message);
          return;
        }
        
        setDeviceStatistics(response.data);
        setDevice(response.data.device);
        
        toast.success('Device data loaded successfully!');
      } catch (err) {
        setError(err.message);
        toast.error('Failed to load device data');
      } finally {
        setLoading(false);
      }
    };

    if (deviceId) {
      loadDeviceData();
    }
  }, [deviceId, selectedDate]);

  useEffect(() => {
    // Update map center when device data is loaded
    if (device?.latitude && device?.longitude) {
      setMapCenter({
        lat: device.latitude,
        lng: device.longitude,
      });
    }
  }, [device]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const getUptimeChartData = (uptimeData) => {
    if (!uptimeData || uptimeData.length === 0) return [];
    const uptime = uptimeData[0];
    const totalDowntime = (uptime.scheduledMaintenance + uptime.unscheduledMaintenance + uptime.unscheduledRepair + uptime.scheduledShutdown) / 3600;
    return [
      { name: 'Uptime', value: uptime.actualUptime / 3600 },
      { name: 'Downtime', value: totalDowntime },
    ].filter(item => item.value > 0);
  };

  const getOperationalHoursChartData = (uptimeData) => {
    if (!uptimeData || uptimeData.length === 0) return [];
    const uptime = uptimeData[0];
    const scheduledDowntime = (uptime.scheduledMaintenance + uptime.scheduledShutdown) / 3600;
    return [
      { name: 'Uptime', value: uptime.actualUptime / 3600 },
      { name: 'Scheduled Downtime', value: scheduledDowntime },
    ].filter(item => item.value > 0);
  };

  const getCriticalHoursChartData = (uptimeData) => {
    if (!uptimeData || uptimeData.length === 0) return [];
    const uptime = uptimeData[0];
    const unscheduledDowntime = (uptime.unscheduledMaintenance + uptime.unscheduledRepair) / 3600;
    return [
      { name: 'Uptime', value: uptime.actualUptime / 3600 },
      { name: 'Unscheduled Downtime', value: unscheduledDowntime },
    ].filter(item => item.value > 0);
  };

  const processEventsForChart = () => {
    const hourlyEvents = new Array(24).fill(0);
    deviceEvents.forEach(event => {
      const hour = new Date(event.date).getHours();
      hourlyEvents[hour]++;
    });
    return hourlyEvents.map((count, hour) => ({
      x: hour,
      y: count
    }));
  };

  const renderUpDownArrows = () => (
    <div className={styles.arrowWrapper}>
      <div className={styles.upBlock}>
        <svg width="145" height="148" viewBox="0 0 145 148">
          <path fillRule="evenodd" fill="rgb(49, 97, 137)" d="M144.987,42.759 C144.989,42.840 144.999,42.919 144.999,43.000 L144.999,138.000 C144.999,143.523 140.523,148.000 135.000,148.000 L9.999,148.000 C4.477,148.000 0.000,143.523 0.000,138.000 L0.000,43.009 C-0.143,40.579 0.704,38.102 2.561,36.245 L2.716,36.164 C4.204,34.578 6.201,33.483 8.449,33.132 L66.246,2.573 C69.693,-0.874 75.283,-0.874 78.731,2.573 L136.518,33.127 C138.791,33.474 140.808,34.581 142.306,36.188 L142.415,36.245 C144.208,38.038 145.059,40.410 144.987,42.759 Z"/>
        </svg>
        <div className={styles.upDownValue}>{deviceStatistics?.generalOperatingData?.TotalUpCount || 0}</div>
        <div className={styles.upDownLabel}>TOTAL UP</div>
      </div>
      <div className={styles.downBlock}>
        <svg width="145" height="148" viewBox="0 0 145 148">
          <path fillRule="evenodd" fill="rgb(49, 97, 137)" d="M142.385,111.755 L142.275,111.813 C140.777,113.418 138.761,114.524 136.490,114.872 L78.713,145.427 C75.267,148.874 69.678,148.874 66.231,145.427 L8.446,114.867 C6.199,114.516 4.204,113.422 2.716,111.837 L2.561,111.755 C0.703,109.897 -0.144,107.418 0.000,104.987 L0.000,10.000 C0.000,4.477 4.477,-0.000 9.999,-0.000 L134.969,-0.000 C140.492,-0.000 144.969,4.477 144.969,10.000 L144.969,105.000 C144.969,105.083 144.959,105.164 144.957,105.247 C145.027,107.594 144.176,109.964 142.385,111.755 Z"/>
        </svg>
        <div className={styles.upDownValue}>{deviceStatistics?.generalOperatingData?.TotalDownCount || 0}</div>
        <div className={styles.upDownLabel}>TOTAL DOWN</div>
      </div>
    </div>
  );

  if (loading || !deviceStatistics) {
    return <ReactiveBuilder.Page loading={true} />;
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className="error-message">
          <h3>Error Loading Device Data</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
      <div className={`${styles.container} overflow-y-auto overflow-x-hidden`}>
      <div className={styles.headerRow}>
        <button onClick={() => navigate('/Buildings')} className={styles.backButton}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path fillRule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8" />
          </svg>
        </button>
        <div style={{ flex: 1 }}>
          <h1 className={styles.headerTitle}>Device Statistics</h1>
          <p className={styles.headerSubtitle}>{device.buildingName} — {device.bankName} — {device.deviceName}</p>
        </div>
        <DatePicker
          className={styles.datePicker}
          value={selectedDate}
          onChange={handleDateChange}
          format="MM/DD/YYYY"
        />
      </div>

      <div className={styles.row}>
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h3 className={styles.cardTitle}>{device.buildingName}</h3>
            <p className={styles.cardSubtitle}>{device.bankName} - {device.deviceName}</p>
          </div>
          <div className={styles.cardContent}>
            {isLoaded ? (
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={mapCenter}
                zoom={mapZoom}
                options={{
                  styles: darkMapStyles,
                  disableDefaultUI: true,
                  zoomControl: true,
                }}
              >
                <Marker
                  position={{
                    lat: device.latitude || defaultLat,
                    lng: device.longitude || defaultLng,
                  }}
                  icon={{
                    url: `./assets/img/markers/${device.status}_details.png`,
                    anchor: new window.google.maps.Point(85, 65),
                    size: new window.google.maps.Size(170, 130)
                  }}
                />
              </GoogleMap>
            ) : (
              <p>Loading Map...</p>
            )}
          </div>
        </div>

        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h3 className={styles.cardTitle}>
              {selectedDate.format('MM/DD/YYYY')}
            </h3>
            <p className={styles.cardSubtitle}>Uptime Performance</p>
          </div>
          <div className={styles.cardContent}>
            <div className={styles.row}>
              <div className={styles.chartItem}>
                <div id="graph-wrapper0" className={styles.chartGraph}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={getUptimeChartData(deviceStatistics.uptime)}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        fill="#8884d8"
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {getUptimeChartData(deviceStatistics.uptime).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className={styles.chartsHeader}>
                  <h3>UPTIME</h3>
                  <small>{selectedDate.format('MM/DD/YYYY')}</small>
                </div>
              </div>
              <div className={styles.chartItem}>
                <div id="graph-wrapper1" className={styles.chartGraph}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={getOperationalHoursChartData(deviceStatistics.uptime)}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        fill="#8884d8"
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {getOperationalHoursChartData(deviceStatistics.uptime).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className={styles.chartsHeader}>
                  <h3>UPTIME</h3>
                  <small>OPERATIONAL HOURS</small>
                </div>
              </div>
              <div className={styles.chartItem}>
                <div id="graph-wrapper2" className={styles.chartGraph}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={getCriticalHoursChartData(deviceStatistics.uptime)}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        fill="#8884d8"
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {getCriticalHoursChartData(deviceStatistics.uptime).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className={styles.chartsHeader}>
                  <h3>UPTIME</h3>
                  <small>CRITICAL HOURS</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {device.type === 'Elevator' && (
        <>
          <div className={styles.row}>
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <h3 className={styles.cardTitle}>NUMBER OF UP AND DOWN DIRECTION</h3>
                <p className={styles.cardSubtitle}>{selectedDate.format('MM/DD/YYYY')}</p>
              </div>
              <div className={styles.cardContent}>
                {renderUpDownArrows()}
              </div>
            </div>

            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <h3 className={styles.cardTitle}>HOURS OF UP, IDLE & DOWN DIRECTION</h3>
                <p className={styles.cardSubtitle}>{selectedDate.format('MM/DD/YYYY')}</p>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.upDownBlock}>
                  <div className={styles.upBlock}>
                    <div className={styles.upDownValue}>{deviceStatistics.upDownHours.UpMinutes}</div>
                    <div className={styles.upDownLabel}>TOTAL UP</div>
                  </div>
                  <div className={styles.idleBlock}>
                    <div className={styles.upDownValue}>{deviceStatistics.upDownHours.StationaryMinutes}</div>
                    <div className={styles.upDownLabel}>IDLE</div>
                  </div>
                  <div className={styles.downBlock}>
                    <div className={styles.upDownValue}>{deviceStatistics.upDownHours.DownMinutes}</div>
                    <div className={styles.upDownLabel}>TOTAL DOWN</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.row}>
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <h3 className={styles.cardTitle}>Number of car calls on {selectedDate.format('MM/DD/YYYY')}</h3>
              </div>
              <div className={styles.cardContent}>
                <div id="liniar-graph">
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={processEventsForChart()}>
                      <XAxis 
                        dataKey="x" 
                        tick={{ fill: 'white' }}
                        tickFormatter={(value) => `${value}:00`}
                      />
                      <YAxis tick={{ fill: 'white' }} />
                      <Tooltip 
                        formatter={(value) => [`${value} calls`, 'Number of Calls']}
                        labelFormatter={(label) => `${label}:00`}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="y" 
                        stroke="#9aaad6" 
                        strokeWidth={2}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <div className={styles.row}>
        <DeviceEventsTable 
          events={deviceEvents} 
          loading={eventsLoading} 
          connected={connected}
        />
      </div>
    </div>
  );
};

export default DeviceStatistics;
